/**
 * A percussion part's drums, as MusicXML says them.
 *
 * MusicXML keeps the two halves of a drum note apart. Where the head sits is on the note,
 * as `<unpitched>` with a display step and octave read exactly as a pitch's would be; what
 * it sounds is in the part list, as one `<score-instrument>` per drum with a
 * `<midi-instrument>` naming the percussion channel and the key on it. The note then points
 * at the instrument by id. So a kit of ours becomes a list of instruments here, and a head
 * that names a drum becomes a head with an `<instrument>` beside it.
 *
 * The channel is 10, which is where General MIDI keeps percussion, and the key is written
 * one higher than the number the GM tables print: `<midi-unpitched>` counts from 1 and the
 * tables count from 0. Getting that wrong moves every drum of the file up a semitone, which
 * on a drum kit means the snare comes back as a rim shot.
 */

import type { NotePitch } from "../document/pitch";
import type { NoteHead, PercussionKit } from "../document/percussion";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";

/** The channel General MIDI keeps percussion on. */
const PERCUSSION_CHANNEL = 10;

/** The id a drum's instrument is written under, within the part's own id. */
export function drumInstrumentId(partId: string, kit: PercussionKit, name: string): string | null {
  const index = kit.notes.findIndex((drum) => drum.name === name);
  return index < 0 ? null : `${partId}-I${index + 1}`;
}

/** The `<score-instrument>` and `<midi-instrument>` pairs of a whole kit, in kit order. */
export function drumInstrumentNodes(partId: string, kit: PercussionKit): XmlNode[] {
  const scored = kit.notes.map((drum, index) =>
    el("score-instrument", { id: `${partId}-I${index + 1}` }, [leaf("instrument-name", drum.name)]),
  );
  const midi = kit.notes.map((drum, index) =>
    el("midi-instrument", { id: `${partId}-I${index + 1}` }, [
      leaf("midi-channel", PERCUSSION_CHANNEL),
      leaf("midi-unpitched", drum.midi + 1),
    ]),
  );
  // Every score-instrument first, then every midi-instrument: the content model of
  // <score-part> puts the two groups in that order rather than pairing them up.
  return [...scored, ...midi];
}

/** Where a drum head sits, which is all MusicXML says about an unpitched note. */
export function unpitchedNode(pitch: NotePitch): XmlNode {
  return el("unpitched", {}, [
    leaf("display-step", pitch.step),
    leaf("display-octave", pitch.octave),
  ]);
}

/** The head a drum is drawn with, or nothing when it is an ordinary one. */
export function noteheadNode(head: NoteHead): XmlNode | null {
  return head === "normal" ? null : leaf("notehead", head);
}
